import { config } from '../config';
import { buildAudioReport } from './audioScenario';
import type { AudioReport } from './audioScenario';
import { round1 } from './stats';
import type { Summary } from './stats';
import { copyArtifactText, downloadArtifact, shareArtifact, stampedFilename } from './shareOut';
import type { Artifact, ExportRoute } from './shareOut';
import type { AudioRunRecord } from '../types';

/**
 * The U-1 spike bundle.
 *
 * Same discipline as U-0's: the ENV-1 admissibility line comes **first**, before any figure, and
 * the A-2 answer comes second. Everything after that is supporting detail. A reader who stops after
 * two lines must not come away thinking a desktop run settled anything.
 *
 * The raw per-chunk and per-probe rows are included in the JSON so the analysis can be redone or
 * disputed without re-recording — which matters more here than for U-0, because these recordings
 * cannot be reproduced at a desk *or* on the device: an interruption segment is the product of one
 * real phone call at one moment.
 */

export interface AudioExportBundle {
  schema: 'touchline.u1.audio-spike';
  version: 1;
  generatedAt: string;
  report: AudioReport;
  runs: AudioRunRecord[];
}

/**
 * @param current the session being reported on — the live one, not a stored one, so the export
 *   always matches what is on screen.
 * @param history previously stored runs, carried for reanalysis. They are **not** merged into the
 *   report: unlike U-0's three conditions, spike runs are not complementary halves of one
 *   measurement, and pooling segments recorded under different permissions or a different app
 *   version would produce a verdict about nothing in particular.
 */
export function buildAudioBundle(
  current: AudioRunRecord,
  history: readonly AudioRunRecord[],
  generatedAt: number,
): AudioExportBundle {
  const seen = new Set([current.id]);
  return {
    schema: 'touchline.u1.audio-spike',
    version: 1,
    generatedAt: new Date(generatedAt).toISOString(),
    report: buildAudioReport(current, generatedAt),
    runs: [current, ...history.filter((r) => !seen.has(r.id))],
  };
}

export function audioBundleToJson(bundle: AudioExportBundle): string {
  return JSON.stringify(bundle, null, 2);
}

function line(label: string, s: Summary | null, unit = 'ms'): string {
  if (!s) return `   ${label}: no samples`;
  return `   ${label}: p50 ${round1(s.p50)}  p95 ${round1(s.p95)}  max ${round1(s.max)} ${unit}  (n=${s.n})`;
}

export function audioBundleToText(bundle: AudioExportBundle): string {
  const r = bundle.report;
  const lines: string[] = [];

  lines.push('TOUCHLINE — build unit U-1 — MediaRecorder spike (assumption A-2)');
  lines.push(bundle.generatedAt);
  lines.push('');

  lines.push(
    r.gate.admissible
      ? 'ENV-1: ADMISSIBLE — measured on the target device as an installed PWA.'
      : 'ENV-1: NOT ACCEPTANCE EVIDENCE.',
  );
  for (const b of r.gate.blockers) lines.push(`  ! ${b}`);
  for (const w of r.gate.warnings) lines.push(`  ~ ${w}`);
  lines.push('');

  lines.push(`A-2: ${r.a2.headline}`);
  lines.push(r.a2.detail);
  lines.push('');

  lines.push('── Checks');
  for (const c of r.checks) {
    lines.push(`   ${c.verdict.toUpperCase().padEnd(12)} ${c.id} — ${c.label}`);
    lines.push(`                observed: ${c.observed}`);
    lines.push(`                expected: ${c.expected}`);
  }
  lines.push('');

  lines.push('── Distributions');
  lines.push(line(`chunk interval (asked for ${config.TIMESLICE_MS} ms)`, r.chunkIntervalMs));
  lines.push(line('chunk size', r.chunkSizeBytes, 'bytes'));
  lines.push(line('cold getUserMedia', r.coldAcquireMs));
  lines.push(line('warm tap → recording', r.warmTapToRecordingMs));
  lines.push('');

  lines.push('── Observations');
  for (const o of r.observations) lines.push(`   • ${o.text}`);
  lines.push('');

  lines.push(
    `── AUD-3 triggers exercised: ${r.triggersCovered.join(', ') || 'none'}${
      r.triggersMissing.length > 0 ? `  ·  STILL MISSING: ${r.triggersMissing.join(', ')}` : ''
    }`,
  );
  lines.push('');

  const run = bundle.runs[0];
  if (run) {
    lines.push(`── Segments (${r.scoreableSegments} scoreable of ${r.totalSegments})`);
    for (const s of run.segments) {
      const full = s.probes.find((p) => p.variant === 'full');
      const trunc = s.probes.find((p) => p.variant === 'truncatedTail');
      lines.push(
        `   ${s.label} · trigger=${s.trigger ?? '—'} · ${s.chunks.length} chunk(s) · ${s.totalBytes} B · mime=${s.actualMime ?? '—'}`,
      );
      lines.push(
        `      full: ${full ? describeProbe(full.canPlayThrough, full.metadataLoaded, full.timedOut) : '—'}` +
          `  ·  truncated: ${trunc ? describeProbe(trunc.canPlayThrough, trunc.metadataLoaded, trunc.timedOut) : '—'}` +
          `  ·  by ear: ${s.manualVerdict}`,
      );
    }
    lines.push('');

    lines.push('── Device');
    lines.push(`   ${run.environment.userAgent}`);
    lines.push(
      `   standalone=${String(run.environment.standalone)} · permission=${run.capabilities.permissionState} · default mime=${run.capabilities.defaultMime ?? '—'}`,
    );
    lines.push(
      `   isTypeSupported: ${run.capabilities.supportMatrix
        .filter((m) => m.supported)
        .map((m) => m.mime)
        .join(', ') || 'none'}`,
    );
    for (const f of run.failures) lines.push(`   ! ${f.stage}: ${f.message}`);
    if (run.notes) lines.push(`   note: ${run.notes}`);
  }

  return lines.join('\n');
}

function describeProbe(canPlay: boolean, metadata: boolean, timedOut: boolean): string {
  if (canPlay) return 'PLAYS';
  if (metadata) return 'metadata only';
  return timedOut ? 'timed out' : 'UNPLAYABLE';
}

function toArtifact(bundle: AudioExportBundle): Artifact {
  return {
    json: audioBundleToJson(bundle),
    text: audioBundleToText(bundle),
    filename: stampedFilename('touchline-u1-audio', Date.parse(bundle.generatedAt)),
    title: 'Touchline U-1 audio spike',
  };
}

/** @throws {import('../app/errors').ExportError} when every route fails or the share is cancelled. */
export async function shareAudioBundle(bundle: AudioExportBundle): Promise<ExportRoute> {
  return shareArtifact(toArtifact(bundle));
}

/** @throws {import('../app/errors').ExportError} */
export function downloadAudioBundle(bundle: AudioExportBundle): void {
  downloadArtifact(toArtifact(bundle));
}

/** @throws {import('../app/errors').ExportError} */
export async function copyAudioBundleText(bundle: AudioExportBundle): Promise<void> {
  await copyArtifactText(toArtifact(bundle));
}
